import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Plus, Trash2, X, Pencil, Coins } from "lucide-react";

const empty = { name: "", email: "", phone: "", license_number: "", vehicle_id: "", user_id: "", active: true };

export default function Drivers() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const fetchAll = async () => {
    const [d, v, u] = await Promise.all([
      api.get("/drivers"),
      api.get("/vehicles"),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setRows(d.data); setVehicles(v.data);
    // Only show users with driver role (or unlinked) in the dropdown
    setUsers((u.data || []).filter((x) => x.role === "driver"));
  };
  useEffect(() => { fetchAll(); }, []);

  const userMap = React.useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u.email; });
    return m;
  }, [users]);

  const openCreate = () => {
    setEditId(null);
    setForm(empty);
    setError("");
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      name: row.name || "",
      email: row.email || "",
      phone: row.phone || "",
      license_number: row.license_number || "",
      vehicle_id: row.vehicle_id || "",
      user_id: row.user_id || "",
      active: row.active !== false,
    });
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        ...form,
        vehicle_id: form.vehicle_id || null,
        user_id: form.user_id || null,
      };
      if (editId) {
        await api.put(`/drivers/${editId}`, payload);
      } else {
        await api.post("/drivers", payload);
      }
      setOpen(false);
      setEditId(null);
      setForm(empty);
      fetchAll();
    } catch (err) { setError(formatApiError(err)); }
  };
  const del = async (id) => {
    if (!window.confirm("Fahrer wirklich löschen?")) return;
    await api.delete(`/drivers/${id}`);
    fetchAll();
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Stammdaten</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Fahrer</h1>
          </div>
          <button onClick={openCreate} data-testid="driver-add-button" className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
            <Plus size={16} strokeWidth={1.8} /> Neuer Fahrer
          </button>
        </div>

        <div className="card mt-6 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">E-Mail</th>
                <th className="text-left py-3 px-4">Telefon</th>
                <th className="text-left py-3 px-4">Führerschein</th>
                <th className="text-left py-3 px-4">Login-Benutzer</th>
                <th className="text-left py-3 px-4">Aktiv</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-[var(--text-3)]">Keine Fahrer erfasst.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`driver-row-${r.id}`}>
                  <td className="py-3 px-4 text-[var(--text)]">{r.name}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{r.email || "—"}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{r.phone || "—"}</td>
                  <td className="py-3 px-4 font-mono text-[var(--text-2)]">{r.license_number || "—"}</td>
                  <td className="py-3 px-4 text-[var(--text)]" data-testid={`driver-userlink-${r.id}`}>
                    {r.user_id
                      ? (userMap[r.user_id] || <span className="text-[var(--text-3)] font-mono text-xs">{r.user_id.slice(0, 8)}…</span>)
                      : <span className="text-red-700 text-xs">⚠ kein Login</span>}
                  </td>
                  <td className="py-3 px-4 text-[var(--text)]">{r.active ? "Ja" : "Nein"}</td>
                  <td className="py-3 px-4 text-right">
                    <Link to={`/admin/drivers/${r.id}/payouts`} data-testid={`driver-payouts-${r.id}`} className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3 inline-flex" title="Pauschalbeträge & Strecken"><Coins size={16} strokeWidth={1.5} /></Link>
                    <button onClick={() => openEdit(r)} data-testid={`driver-edit-${r.id}`} className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3"><Pencil size={16} strokeWidth={1.5} /></button>
                    <button onClick={() => del(r.id)} data-testid={`driver-delete-${r.id}`} className="text-[var(--text-3)] hover:text-red-700"><Trash2 size={16} strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-xl p-6 fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">{editId ? "Fahrer bearbeiten" : "Neuer Fahrer"}</h2>
              <button onClick={() => setOpen(false)} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18} /></button>
            </div>
            {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm mb-4">{error}</div>}
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="label-eyebrow block mb-2">Name *</label><input required data-testid="driver-form-name" className="input-base" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">E-Mail</label><input type="email" className="input-base" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Telefon</label><input className="input-base" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
              <div><label className="label-eyebrow block mb-2">Führerschein Nr.</label><input className="input-base" value={form.license_number} onChange={(e) => setForm({...form, license_number: e.target.value})} /></div>
              <div>
                <label className="label-eyebrow block mb-2">Fahrzeug</label>
                <select className="input-base" value={form.vehicle_id || ""} onChange={(e) => setForm({...form, vehicle_id: e.target.value})}>
                  <option value="">—</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate} ({v.model})</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-2">Verknüpfter Login-Benutzer</label>
                <select
                  data-testid="driver-form-user"
                  className="input-base"
                  value={form.user_id || ""}
                  onChange={(e) => setForm({...form, user_id: e.target.value})}
                >
                  <option value="">— Kein Login (Fahrer kann sich nicht einloggen) —</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.email} · {u.name || ""}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[var(--text-3)] mt-1.5">
                  Damit ein Fahrer Aufträge sieht und Statusupdates senden kann, muss er hier mit einem
                  <span className="text-[#D4AF37]"> Benutzer mit Rolle „Fahrer"</span> verknüpft sein.
                  Falls noch kein Login existiert: zuerst unter <strong>Benutzer</strong> einen Account mit Rolle „Fahrer" anlegen.
                </p>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="driver-active" data-testid="driver-form-active" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} />
                <label htmlFor="driver-active" className="text-sm text-[var(--text)]">Aktiv</label>
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-sm rounded-sm uppercase tracking-wider">Abbrechen</button>
                <button type="submit" data-testid="driver-form-submit" className="btn-gold px-4 py-2 text-sm rounded-sm uppercase tracking-wider">{editId ? "Speichern" : "Anlegen"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
